import { usePage } from "@/components/page/PageContext";
import { Button } from "@/components/ui/button";
import {
  Card,
  ExploreDisplay,
  MultiSeriesChart,
  SingleSeriesChart,
} from "@/pages/data/DataChart";
import { useBaggageCarousels } from "@/queries/baggage";
import { getAggregateData, ZoneQuery } from "@/queries/report";
import { useReportContext } from "@/stores/ReportContext";
import { useSessionData } from "@/stores/SessionDataContext";
import { AlertCircle } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

function TrafficChart({ query, title }: { query: ZoneQuery; title: string }) {
  const { reportId } = useReportContext();

  const { data: trafficData, isSuccess: trafficSuccess } = getAggregateData(
    [
      {
        query,
      },
    ],
    reportId,
    true
  );

  if (!trafficSuccess) {
    return null;
  }

  return (
    <Card title={title}>
      <SingleSeriesChart
        type={"column"}
        values={trafficData[0].values}
        labels={trafficData[0].labels}
        xAxisTitle={"Time"}
        yAxisTitle={"Passengers"}
      />
    </Card>
  );
}

function MultiBaggagePassengers() {
  const { reportId } = useReportContext();
  const { airport } = useSessionData();
  let baggageNames = [];
  const { data, isSuccess } = getAggregateData(
    airport.baggage_halls.map((t) => {
      baggageNames.push(t.name);
      return {
        query: {
          measure: "pax",
          baggage_id: t.id,
          type: "baggage",
        },
      };
    }),
    reportId
  );

  if (!isSuccess || data.length === 0) {
    return null;
  }
  return (
    <Card title="Baggage Claim Hall - Passenger Volume">
      <MultiSeriesChart
        series={data.map((d, i) => ({
          name: baggageNames[i],
          values: d.values,
        }))}
        labels={data[0].labels}
        xAxisTitle={"Time"}
        yAxisTitle={"Passengers"}
        totalRequired={true}
        chartType="column"
      />
    </Card>
  );
}

function BaggageCarouselMetrics() {
  const { data: carousels, isLoading } = useBaggageCarousels();

  if (isLoading) {
    return null;
  }

  if (!carousels || carousels.length === 0) {
    return (
      <div className="col-span-full">
        <Card title="Baggage Carousel - Configuration Required">
          <div className="flex items-center justify-center p-12 text-center">
            <div>
              <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">No Carousels Setup</p>
              <p className="text-sm text-muted-foreground mb-4">
                Please configure baggage carousels to view this data
              </p>
              <Link to="/setup/airport?tab=baggage">
                <Button variant="secondary">Configure Carousels →</Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="col-span-full">
        <Card title="Baggage Carousel - Passengers">
          <ExploreDisplay
            height={800}
            query={{
              type: "baggage_carousel",
              measure: "pax",
              direction: "arr",
            }}
          />
        </Card>
      </div>
      <div className="col-span-full">
        <Card title="Baggage Carousel - Bags">
          <ExploreDisplay
            height={800}
            query={{
              type: "baggage_carousel",
              measure: "bag",
              direction: "arr",
            }}
          />
        </Card>
      </div>
    </>
  );
}

export function BaggageOverview() {
  const { setTitles } = usePage();
  const { airport } = useSessionData();
  const multiBaggage = airport.baggage_halls.length > 1;

  useEffect(() => {
    setTitles(["Baggage Overview"]);
  }, []);

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 space-y-4">
      <div className="h-auto md:h-90 col-span-full p-0">
        {multiBaggage ? (
          <MultiBaggagePassengers />
        ) : (
          <TrafficChart
            query={{
              type: "baggage",
              measure: "pax",
            }}
            title={"Baggage Claim Hall - Passenger Volume"}
          />
        )}
      </div>
      <BaggageCarouselMetrics />
    </div>
  );
}
